"use client";

import {
  Cpu,
  Eye,
  Fingerprint,
  Globe,
  Sparkles,
  Video,
  Zap,
} from "lucide-react";
import { useJsonTranslation } from "@/hooks/useJsonTranslation";

const features = [
  {
    icon: Video,
    titleKey: "features.fullVideoAnalysis.title",
    descriptionKey: "features.fullVideoAnalysis.description",
    color: "leaf",
  },
  {
    icon: Fingerprint,
    titleKey: "features.yourVoiceAmplified.title",
    descriptionKey: "features.yourVoiceAmplified.description",
    color: "leaf",
  },
  {
    icon: Zap,
    titleKey: "features.preUploadOptimization.title",
    descriptionKey: "features.preUploadOptimization.description",
    color: "leaf",
  },
  {
    icon: Globe,
    titleKey: "features.globalReach.title",
    descriptionKey: "features.globalReach.description",
    color: "leaf",
  },
  {
    icon: Sparkles,
    titleKey: "features.meaningFirstTechnology.title",
    descriptionKey: "features.meaningFirstTechnology.description",
    color: "amber",
  },
  {
    icon: Cpu,
    titleKey: "features.multiModelAi.title",
    descriptionKey: "features.multiModelAi.description",
    color: "leaf",
  },
];

const colorMap: Record<string, string> = {
  leaf: "bg-surface-success border-surface-success-border text-leaf-700 dark:text-leaf-300",
  amber:
    "bg-surface-warning border-surface-warning-border text-leaf-600 dark:text-leaf-400",
};

export function WhyDifferent() {
  const { t } = useJsonTranslation("HomeWhyDifferent");

  return (
    <div data-home-block="why-different" className="relative">
      <div className="mx-auto max-w-4xl">
        <div className="mb-10 text-center">
          <h2 className="home-section-title text-3xl text-balance md:text-4xl lg:text-5xl">
            {t("title")}
          </h2>
          <p className="home-section-copy mx-auto mt-5 max-w-2xl text-lg text-pretty">
            {t("subtitle")}
          </p>
        </div>

        {/* Competitor contrast — "them vs us" at a glance */}
        <div className="mx-auto mb-12 grid max-w-3xl gap-4 sm:grid-cols-2">
          <div className="uni-card group relative overflow-hidden">
            <div className="text-muted-foreground/60 mb-3 text-xs font-semibold uppercase">
              {t("contrast.themLabel")}
            </div>
            <div className="mb-3 flex items-start gap-3">
              <div className="border-border/40 bg-surface-soft text-muted-foreground/70 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border">
                <Eye className="h-5 w-5" aria-hidden="true" />
              </div>
              <h3 className="text-foreground/80 text-base font-semibold">
                {t("contrast.themTitle")}
              </h3>
            </div>
            <p className="home-section-copy text-sm">
              {t("contrast.themBody")}
            </p>
          </div>

          <div className="uni-card group border-leaf-500/30 ring-leaf-500/15 relative overflow-hidden !border-[1.5px] ring-1">
            <div className="text-leaf-600 dark:text-leaf-400 mb-3 text-xs font-semibold uppercase">
              {t("contrast.usLabel")}
            </div>
            <div className="mb-3 flex items-start gap-3">
              <div className="home-icon-tile h-10 w-10 shrink-0">
                <Video className="h-5 w-5" aria-hidden="true" />
              </div>
              <h3 className="text-foreground text-base font-semibold">
                {t("contrast.usTitle")}
              </h3>
            </div>
            <p className="home-section-copy text-sm">
              {t("contrast.usBody")}
            </p>
          </div>
        </div>
      </div>

      <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
        {features.map((feature, i) => (
          <div key={i} className="uni-card group">
            <div
              className={`home-icon-tile h-12 w-12 ${colorMap[feature.color]} mb-5 transition-transform duration-300 group-hover:scale-105`}
            >
              <feature.icon className="h-6 w-6" />
            </div>
            <h3 className="font-display text-foreground mb-2.5 text-lg font-semibold">
              {t(feature.titleKey)}
            </h3>
            <p className="home-section-copy text-sm">
              {t(feature.descriptionKey)}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}
